import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { photoCategory } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// Claim Photos - Main photos table
export const claimPhotos = pgTable("claim_photos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Claim association
  claimId: uuid("claim_id").notNull().references(() => claims.id, { onDelete: "cascade" }),
  
  // Storage information
  storageKey: varchar("storage_key").notNull(), // Object storage key
  publicUrl: text("public_url").notNull(), // Public URL for display
  thumbnailUrl: text("thumbnail_url"), // Thumbnail version URL
  
  // File metadata
  fileName: varchar("file_name").notNull(),
  mimeType: varchar("mime_type").notNull(),
  fileSize: integer("file_size"), // Size in bytes
  width: integer("width"), // Image width in pixels
  height: integer("height"), // Image height in pixels
  
  // Photo metadata
  caption: text("caption"), // User-provided caption
  notes: text("notes"), // Detailed notes about the photo
  category: photoCategory("category").default("general"),
  
  // GPS/Location data
  latitude: decimal("latitude", { precision: 10, scale: 7 }), // GPS latitude
  longitude: decimal("longitude", { precision: 10, scale: 7 }), // GPS longitude
  altitude: decimal("altitude", { precision: 8, scale: 2 }), // GPS altitude in meters
  locationAccuracy: decimal("location_accuracy", { precision: 6, scale: 2 }), // GPS accuracy in meters
  address: text("address"), // Reverse geocoded address
  
  // Timestamp data
  capturedAt: timestamp("captured_at").defaultNow(), // When photo was taken
  deviceTimestamp: timestamp("device_timestamp"), // Original device timestamp from EXIF
  
  // Device/Source info
  deviceInfo: varchar("device_info"), // Device model/info
  sourceType: varchar("source_type").default("camera"), // camera, upload, import
  
  // Annotation data (stored as JSON for canvas-based annotations)
  annotations: jsonb("annotations"), // Array of annotation objects {type, x, y, data, color, etc.}
  hasAnnotations: boolean("has_annotations").default(false),
  annotatedUrl: text("annotated_url"), // URL to annotated version if generated
  
  // Tags for organization
  tags: text("tags").array(), // Array of tag strings
  
  // Before/After pairing
  pairedPhotoId: uuid("paired_photo_id"), // Link to before/after companion photo
  pairType: varchar("pair_type"), // 'before' or 'after'
  
  // Sharing
  shareToken: varchar("share_token").unique(), // For public sharing links
  shareExpiresAt: timestamp("share_expires_at"), // When share link expires
  isPubliclyShared: boolean("is_publicly_shared").default(false),
  
  // AI Vision analysis
  aiAnalysis: jsonb("ai_analysis"), // AI-generated analysis results
  aiAnalyzedAt: timestamp("ai_analyzed_at"),
  aiTags: text("ai_tags").array(), // AI-generated tags
  aiDescription: text("ai_description"), // AI-generated description
  
  // Training data for AI
  userCorrectedAiTags: text("user_corrected_ai_tags").array(), // User corrections to AI tags
  isTrainingData: boolean("is_training_data").default(false), // Marked for AI training
  trainingExportedAt: timestamp("training_exported_at"),
  
  // Audit
  uploadedBy: varchar("uploaded_by").references(() => users.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"), // Soft delete
}, (table) => [
  index("idx_claim_photos_claim").on(table.claimId),
  index("idx_claim_photos_category").on(table.category),
  index("idx_claim_photos_captured").on(table.capturedAt),
  index("idx_claim_photos_location").on(table.latitude, table.longitude),
  index("idx_claim_photos_share_token").on(table.shareToken),
  index("idx_claim_photos_tags").on(table.tags),
  index("idx_claim_photos_uploaded_by").on(table.uploadedBy),
]);


// Claim Photo Comments
export const claimPhotoComments = pgTable("claim_photo_comments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  photoId: uuid("photo_id").notNull().references(() => claimPhotos.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id),
  content: text("content").notNull(),
  
  // Position on photo for anchored comments
  positionX: decimal("position_x", { precision: 5, scale: 2 }), // Percentage 0-100
  positionY: decimal("position_y", { precision: 5, scale: 2 }), // Percentage 0-100
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_photo_comments_photo").on(table.photoId),
  index("idx_photo_comments_user").on(table.userId),
]);
